import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from '@material-ui/core/Modal';
import Backdrop from '@material-ui/core/Backdrop';
import Close from '@material-ui/icons/Close';
import Carousel from 'nuka-carousel';
import styled from 'styled-components';
import { biggerThanOrEqualTo, smallerThanOrEqualTo } from '../../../core/util/styledComponents';
import { fontSize } from '../../../../src/constants/text';
import ChevronRight from '@material-ui/icons/ChevronRight';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import { formatLocationImageTitle, formatLocationImageAlt } from '../../../core/util/seo';
import { analyticsTrackEvent } from '../../../core/analytics';
import { CDP_CDP_PHOTO_CAROUSEL } from '../../../core/analytics/events';
import { isEmptyObject } from '../../../core/util/object';
import { getImageObject, PHOTO } from '../Picture/util';
import Picture from '../Picture';
import { isEmptyArray } from '../../../core/util/array';

const ARROW_RIGHT = 39;
const ARROW_LEFT = 37;

const CarouselContainer = styled.div`
  outline: none;
  ${biggerThanOrEqualTo.md`
      width: 80%;
      max-width: 1024px;
      max-height: 90%;
      & .slider-control-bottomcenter {
        display: none;
      }
  `};
  ${smallerThanOrEqualTo.md`
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;

      & .slider-control-bottomcenter {
        bottom: -20% !important;
        & button {
          color: white !important;
        }
      }
  `};
`;

const ExternalCloseButton = styled.div`
  ${biggerThanOrEqualTo.md`
    display: none;
  `};
  ${smallerThanOrEqualTo.md`
    position: absolute;
    top: 30px;
    right: 30px;
    color: white;
    > svg {
      font-size: ${fontSize.extraExtraLarge};
    }
  `};
`;

const CenteredModal = styled(Modal)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledBackdrop = styled(Backdrop)`
  ${smallerThanOrEqualTo.md`
    background-color: black;
  `};
`;

const ImageContainer = styled.div`
  display: inline-block;
  width: 100%;
  text-align: center;
`;

const SizedPicture = styled(Picture)`
  ${smallerThanOrEqualTo.md`
    max-width: 100%;
  `};
`;

const CircleBackground = styled.div`
  width: 72px;
  height: 72px;
  background-color: rgba(65, 79, 96, 0.5);
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LeftCircleBackground = styled(CircleBackground)`
  left: -100px;
`;

const RightCircleBackground = styled(CircleBackground)`
  right: -100px;
`;

const StyledChevronLeft = styled(ChevronLeft)`
  position: absolute;
  font-size: 50px;
  color: white;
`;

const StyledChevronRight = styled(ChevronRight)`
  position: absolute;
  font-size: 50px;
  color: white;
`;

const StyledClose = styled(Close)`
  position: absolute;
  font-size: 50px;
  color: white;
  right: -80px;
  top: -50px;
`;

class PhotoCarousel extends Component {
  state = {
    slideIndex: 0,
  };

  componentDidMount = () => {
    document.addEventListener('keydown', this.handleKeyDown);
  };

  componentWillUnmount = () => {
    document.removeEventListener('keydown', this.handleKeyDown);
  };

  getImageData() {
    const { viewport, location } = this.props;
    const { imageLinks } = location;
    const photos =
      isEmptyObject(imageLinks) || isEmptyArray(imageLinks.image) ? [] : imageLinks.image;
    const img = [];

    for (let i = 0; i < photos.length; i++) {
      img.push(
        <ImageContainer itemScope itemType="http://schema.org/ImageObject" key={photos[i]}>
          <meta itemProp="name" content={location.name} />
          <meta itemProp="contentLocation" content={`${location.city}, ${location.state}`} />
          <meta
            itemProp="description"
            content={`Photo of ${location.name} (${location.city}, ${location.state})`}
          />
          <SizedPicture
            imageObject={getImageObject(location, {
              imageType: PHOTO,
              viewport,
              imageIndex: i,
            })}
            role="presentation"
            title={formatLocationImageTitle(location)}
            alt={formatLocationImageAlt(location)}
            itemProp="contentUrl"
          />
        </ImageContainer>
      );
    }

    return img;
  }

  handleKeyDown = event => {
    switch (event.keyCode) {
      case ARROW_RIGHT:
        this.setState(state => ({ slideIndex: state.slideIndex + 1 }));
        break;
      case ARROW_LEFT:
        this.setState(state => ({ slideIndex: state.slideIndex - 1 }));
        break;
      default:
        break;
    }
  };

  // We need to trigger the resize event manually
  // because of this issue: https://github.com/FormidableLabs/nuka-carousel/issues/103
  triggerResizeEvent = () => {
    window.dispatchEvent(new Event('resize'));
  };

  trackSlideChange = slideIndex =>
    this.setState({ slideIndex }, () => {
      if (slideIndex === 1) {
        analyticsTrackEvent(CDP_CDP_PHOTO_CAROUSEL);
      }
    });

  renderCenterLeftControl = ({ previousSlide }) => (
    <LeftCircleBackground>
      <StyledChevronLeft onClick={previousSlide} />
    </LeftCircleBackground>
  );

  renderCenterRightControl = ({ nextSlide }) => (
    <RightCircleBackground>
      <StyledChevronRight onClick={nextSlide} />
    </RightCircleBackground>
  );

  renderTopRightControl = onClickCallback => <StyledClose onClick={onClickCallback} />;

  render = () => {
    const { slideIndex } = this.state;
    const { open, handleClose } = this.props;

    return (
      <CenteredModal
        aria-labelledby="Provider location photo carousel"
        open={open}
        onClose={handleClose}
        BackdropComponent={StyledBackdrop}
        disableAutoFocus
      >
        <CarouselContainer>
          <Carousel
            heightMode="current"
            slideIndex={slideIndex}
            afterSlide={this.trackSlideChange}
            renderCenterLeftControls={this.renderCenterLeftControl}
            renderCenterRightControls={this.renderCenterRightControl}
            renderTopRightControls={() => this.renderTopRightControl(handleClose)}
          >
            {this.getImageData()}
          </Carousel>
          <ExternalCloseButton onClick={handleClose}>
            <Close />
          </ExternalCloseButton>
        </CarouselContainer>
      </CenteredModal>
    );
  };
}

export default PhotoCarousel;

PhotoCarousel.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  photos: PropTypes.array.isRequired,
  location: PropTypes.object.isRequired,
  viewport: PropTypes.object,
};
